import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import { Truck, User, Phone, ExternalLink, Package, RefreshCw, XCircle, MapPin, HelpCircle } from "lucide-react";
import OrderProgressBar from "../components/OrderProgressBar";
import { getSocketOrigin } from "../lib/socketOrigin";
import { groupOrderItems, ORDER_TERMINAL_STATUS_KEYS } from "../lib/orderDetailsHelpers";
import { getStatusSoftStyle } from "../lib/statusColors";
import { getTrackingUrl } from "../lib/carrierTracking";
import { resolveProviderStatusText } from "../lib/providerStatusLabels";

// Ported from client/src/features/orders/pages/PublicOrderTrackingPage.jsx
// (the seller-portal's own public tracking page) so the SAME order can be
// tracked from either domain — app.reevoxi.com/track/:orderId (unchanged,
// always works) or this storefront's own domain, once a seller sets
// Settings → Web Order → Custom Domain (see SellerWebOrderSetting.
// customDomain and ChatflowEngineService.executeDropitCreateOrder's tracking
// link). Both call the exact same public, unauthenticated backend endpoint
// (GET /public/orders/:orderId) and the same public /public-tracking socket
// namespace — no seller-key/auth needed for either, same as the original.
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:9000";

// The progress bar only shows the seller's forward-moving statuses — the two
// terminal side-states (reached via an explicit Cancel/Return action, never
// as a "next step") are excluded.
const TERMINAL_STATUS_KEYS = new Set(ORDER_TERMINAL_STATUS_KEYS);

// Last-resort fallback only — used if the tracking API response predates the
// orderStatuses field (or the order has no seller attached) and the seller's
// real configured statuses genuinely aren't available.
const FALLBACK_STEPS = [
  { key: "Received", label: "Received" },
  { key: "Rider Assigned", label: "Rider Assigned" },
  { key: "Packed", label: "Packed" },
  { key: "Out for Delivery", label: "Out for Delivery" },
  { key: "Delivered", label: "Delivered" },
];

// Dropit's own "Allowed Cancel States" (per their API docs) — a ride can
// only still be cancelled while it's in one of these.
const DROPIT_CANCELLABLE_STATUSES = ["Pending", "Accepted", "ArrivedAtPickup"];

// Rider Details is only meaningful while a rider is genuinely assigned/
// en-route (or already delivered) — pilotName/pilotPhone are never cleared
// in the database once a rider first accepts, even across a cancel +
// DropitAutoRecreateService auto-recreate cycle, so checking those two
// fields alone kept showing a rider who'd already cancelled/aborted (or
// hadn't even been assigned yet on the freshly re-created ride) as if they
// were still live and callable — customers were calling a rider who'd
// already left. Gated on shipment.status instead (display-only; the
// underlying pilotName/pilotPhone data itself is untouched, still there
// for whenever a genuinely active rider is assigned again).
const RIDER_DETAILS_VISIBLE_STATUSES = new Set(["Accepted", "ArrivedAtPickup", "InProgress", "Completed"]);

export default function PublicOrderTrackingPage() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [storeLogoError, setStoreLogoError] = useState(false);

  const fetchTracking = useCallback(
    (isRefresh) => {
      if (!orderId) return;
      if (isRefresh) setRefreshing(true);
      else setIsLoading(true);
      setError(null);
      return axios
        .get(`${API_BASE}/public/orders/${orderId}`)
        .then(({ data: res }) => setData(res.data ?? res))
        .catch(() => setError("Order not found"))
        .finally(() => (isRefresh ? setRefreshing(false) : setIsLoading(false)));
    },
    [orderId]
  );

  useEffect(() => {
    fetchTracking(false);
  }, [fetchTracking]);

  useEffect(() => {
    if (!orderId) return;
    const socket = io(`${getSocketOrigin()}/public-tracking`, {
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => socket.emit("join_order_tracking", orderId));
    socket.on("order_tracking_updated", () => fetchTracking(true));
    return () => {
      socket.emit("leave_order_tracking", orderId);
      socket.disconnect();
    };
  }, [orderId, fetchTracking]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-sm font-semibold text-gray-400">Loading tracking details…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-20 text-center">
        <p className="text-lg font-semibold text-gray-700">{error ?? "Order not found"}</p>
      </div>
    );
  }

  const { storeName, storeLogo, storePhone, order, shipment, orderStatuses, providerStatus } = data;
  const resolveUrl = (img) => (img ? (img.startsWith("http") ? img : `${API_BASE}${img}`) : null);
  const storeLogoSrc = resolveUrl(storeLogo);
  const orderStatus = order?.status;
  const statusInfo = orderStatuses?.find((s) => s.key === orderStatus);
  const style = getStatusSoftStyle(statusInfo?.color);
  const orderStatusLabel = statusInfo?.label ?? orderStatus;
  const progressSteps = Array.isArray(orderStatuses) && orderStatuses.length
    ? orderStatuses.filter((s) => !TERMINAL_STATUS_KEYS.has(s.key))
    : FALLBACK_STEPS;
  const cancelledLabel = orderStatuses?.find((s) => s.key === "Cancelled")?.label;
  const providerStatusText = resolveProviderStatusText(providerStatus);
  const manualTrackingUrl = getTrackingUrl(order?.shippingProvider, order?.trackingNumber);

  const displayFare = shipment?.estimatedFareAmount ?? shipment?.fareAmount;
  const etaLow = shipment?.estimatedDurationMinutes != null ? Math.round(shipment.estimatedDurationMinutes) : null;
  const etaBuffer = shipment?.etaBufferMinutes ?? 7;
  const etaRange = etaLow != null ? `${etaLow + etaBuffer} min` : null;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-2xl space-y-6">
        {(storeName || storeLogoSrc || storePhone) && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              {storeLogoSrc && !storeLogoError ? (
                <img
                  src={storeLogoSrc}
                  alt={storeName || "Store logo"}
                  className="h-9 w-auto flex-shrink-0 object-contain sm:h-11"
                  onError={() => setStoreLogoError(true)}
                />
              ) : storeName ? (
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-extrabold text-brand-700 ring-1 ring-brand-100 sm:h-11 sm:w-11">
                  {storeName.charAt(0).toUpperCase()}
                </div>
              ) : null}
            </div>
            {storePhone && (
              <a
                href={`tel:${storePhone}`}
                className="flex flex-shrink-0 items-center gap-1 text-xs text-gray-600 transition-colors hover:text-brand-700 sm:gap-1.5 sm:text-sm"
              >
                <HelpCircle className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
                <span className="text-gray-500">Need Help?</span>
                <span className="font-semibold text-gray-900">{storePhone}</span>
              </a>
            )}
          </div>
        )}

        <div className="rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm">
          <OrderProgressBar order={order} steps={progressSteps} cancelledLabel={cancelledLabel} />
        </div>

        <div className="rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50">
                <Truck className="h-5 w-5 text-violet-600" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold text-gray-900">Track Your Delivery</h1>
                {order?.orderNumber && <p className="truncate text-xs text-gray-400">Order #{order.orderNumber}</p>}
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              <span
                className={
                  providerStatusText
                    ? "whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-bold text-gray-600"
                    : `whitespace-nowrap rounded-full border px-3 py-1 text-xs font-bold ${style.bg} ${style.text} ${style.border}`
                }
              >
                {providerStatusText || orderStatusLabel}
              </span>
              <button
                type="button"
                onClick={() => fetchTracking(true)}
                disabled={refreshing}
                aria-label="Refresh delivery status"
                className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          <div className="space-y-2.5 text-sm">
            {etaRange && orderStatus !== "Delivered" && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Estimated Time</span>
                <span className="text-gray-700">{etaRange}</span>
              </div>
            )}
            {(shipment?.pilotName || shipment?.pilotPhone) && RIDER_DETAILS_VISIBLE_STATUSES.has(shipment?.status) && (
              <div className="pt-2 border-t border-gray-100">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-500">Rider Details</p>
                  {shipment?.trackingUrl && (
                    <a
                      href={shipment.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Open Live Tracking <MapPin className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {shipment?.pilotName && (
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {shipment.pilotName}
                    </span>
                  )}
                  {shipment?.pilotPhone && (
                    <a
                      href={`tel:${shipment.pilotPhone}`}
                      className="flex items-center gap-1.5 text-gray-700 transition-colors hover:text-brand-700"
                    >
                      <Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {shipment.pilotPhone}
                    </a>
                  )}
                </div>
              </div>
            )}
            {!shipment && order.shippingProvider && order.trackingNumber && (
              <div className="pt-2 border-t border-gray-100">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-500">Shipping</p>
                  {manualTrackingUrl && (
                    <a
                      href={manualTrackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                    >
                      Track Shipment <MapPin className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <p className="flex items-center gap-1.5 text-gray-700">
                  <Truck className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  {order.shippingProvider} — {order.trackingNumber}
                </p>
              </div>
            )}
            {order.shippingAddress && (
              <p className="pt-2 border-t border-gray-100 text-sm text-gray-700">
                Delivering to: {order.shippingAddress}
                {order.shippingCity ? `, ${order.shippingCity}` : ""}
              </p>
            )}
          </div>
        </div>

        {order && (
          <div className="rounded-2xl border-2 border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-50">
                <Package className="h-4 w-4 text-gray-500" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Order Summary</h2>
            </div>

            {Array.isArray(order.items) && order.items.length > 0 && (
              <div className="space-y-2 mb-4">
                {groupOrderItems(order.items).map(({ item, variantLabels }, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="min-w-0 text-gray-700">
                      {item.name} <span className="font-bold text-gray-900">× {item.quantity || 1}</span>
                      {variantLabels.length > 0 && (
                        <span className="block text-xs font-bold text-gray-900">{variantLabels.join(", ")}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-gray-900 font-medium">
                      ₹{(Number(item.price) * Number(item.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1.5 border-t border-gray-100 pt-3 text-sm">
              {(displayFare ?? order.shippingFee) != null && (
                <div className="flex items-center justify-between text-gray-500">
                  <span>Delivery Charge</span>
                  <span>
                    {order.freeDelivery ? (
                      <>
                        <span className="text-gray-400 line-through mr-1">
                          ₹{Number(displayFare ?? order.shippingFee).toFixed(2)}
                        </span>
                        Free
                      </>
                    ) : (
                      `₹${Number(displayFare ?? order.shippingFee).toFixed(2)}`
                    )}
                  </span>
                </div>
              )}
              {order.gstAmount > 0 && (
                <div className="flex items-center justify-between text-gray-500">
                  <span>{order.gstInclusive ? "GST (Including)" : "GST"}</span>
                  <span>₹{Number(order.gstAmount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between font-semibold text-gray-900">
                <span>Total</span>
                <span>₹{Number(order.grandTotal ?? order.totalAmount).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between font-bold text-gray-900">
                <span>Final Total (Rounded)</span>
                <span>₹{order.roundedTotal ?? Math.round(order.grandTotal ?? order.totalAmount)}</span>
              </div>
            </div>
          </div>
        )}

        {storePhone && DROPIT_CANCELLABLE_STATUSES.includes(shipment?.status) && (
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-sm font-bold text-red-700">Want to cancel this order?</p>
            <p className="mt-1 text-xs text-red-600">
              Only the seller can cancel your order — please contact them directly.
            </p>
            <a
              href={`tel:${storePhone}`}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-700"
            >
              <Phone className="h-4 w-4" /> Call {storePhone}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
